import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "../../config/axiosClient";
import Config from "../../config/Config";

const API_BASE = Config().url;

const formatTimestamp = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now - d;
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMin / 60);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDays = Math.floor(diffHr / 24);
  if (diffDays === 1) return "yesterday";
  if (diffDays <= 30) return `${diffDays}d ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const TYPE_ICONS = {
  comment: "\u2709",
  like: "\u2665",
};

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  const fetchCount = useCallback(() => {
    axios
      .get(`${API_BASE}api/v1/notifications/unread-count/`)
      .then((res) => setUnreadCount(res.data.count || 0))
      .catch(() => {});
  }, []);

  const fetchNotifications = useCallback(() => {
    axios
      .get(`${API_BASE}api/v1/notifications/`)
      .then((res) => {
        setNotifications(res.data || []);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, []);

  // Fetch unread count on mount and every 60s
  useEffect(() => {
    fetchCount();
    const interval = setInterval(fetchCount, 60000);
    return () => clearInterval(interval);
  }, [fetchCount]);

  // Load full list when dropdown opens
  useEffect(() => {
    if (open && !loaded) fetchNotifications();
  }, [open, loaded, fetchNotifications]);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const markAllRead = () => {
    axios
      .post(`${API_BASE}api/v1/notifications/mark-all-read/`)
      .then(() => {
        setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
        setUnreadCount(0);
      })
      .catch(() => {});
  };

  const handleClick = (n) => {
    // Mark as read
    if (!n.is_read) {
      axios
        .patch(`${API_BASE}api/v1/notifications/${n.id}/read/`)
        .then(() => {
          setNotifications((prev) =>
            prev.map((x) => (x.id === n.id ? { ...x, is_read: true } : x))
          );
          setUnreadCount((c) => Math.max(0, c - 1));
        })
        .catch(() => {});
    }
    // Navigate
    if (n.link) {
      setOpen(false);
      navigate(n.link);
    }
  };

  return (
    <div ref={ref} style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
      <button
        onClick={() => {
          setOpen(!open);
          if (!open && !loaded) fetchNotifications();
        }}
        style={{
          background: "none",
          border: "none",
          cursor: "pointer",
          position: "relative",
          padding: 4,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        title="Notifications"
      >
        <svg
          width="20" height="20" viewBox="0 0 24 24"
          fill="none" stroke="var(--ink, #333)" strokeWidth="2"
          strokeLinecap="round" strokeLinejoin="round"
        >
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unreadCount > 0 && (
          <span style={{
            position: "absolute", top: 0, right: 0,
            background: "#ef4444", color: "#fff",
            borderRadius: "50%", minWidth: 16, height: 16,
            fontSize: 10, fontWeight: 700,
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: "0 4px",
          }}>
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div style={{
          position: "absolute", top: "100%", right: 0,
          marginTop: 8, width: 340,
          background: "var(--surface, #fff)",
          border: "1px solid var(--border, #ddd)",
          borderRadius: 10,
          boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
          zIndex: 2000,
          maxHeight: 420,
          display: "flex",
          flexDirection: "column",
        }}>
          {/* Header */}
          <div style={{
            padding: "12px 16px",
            borderBottom: "1px solid var(--border, #eee)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}>
            <span style={{ fontWeight: 700, fontSize: 14, color: "var(--ink)" }}>
              Notifications
            </span>
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                style={{
                  background: "none", border: "none",
                  color: "var(--brand, #59c6c1)", fontSize: 12,
                  cursor: "pointer", fontWeight: 600,
                }}
              >
                Mark all read
              </button>
            )}
          </div>

          {/* List */}
          <div style={{ overflowY: "auto", flex: 1 }}>
            {!loaded ? (
              <p style={{ padding: 16, color: "var(--muted, #888)", fontSize: 13, textAlign: "center" }}>
                Loading...
              </p>
            ) : notifications.length === 0 ? (
              <p style={{ padding: 16, color: "var(--muted, #888)", fontSize: 13, textAlign: "center" }}>
                No notifications yet.
              </p>
            ) : (
              notifications.map((n) => (
                <div
                  key={n.id}
                  onClick={() => handleClick(n)}
                  style={{
                    padding: "10px 16px",
                    borderBottom: "1px solid var(--border, #f0f0f0)",
                    cursor: n.link ? "pointer" : "default",
                    background: n.is_read ? "transparent" : "var(--bg-soft, #f8f9fa)",
                    display: "flex",
                    gap: 10,
                    alignItems: "flex-start",
                  }}
                >
                  {/* Avatar */}
                  {n.actor_picture ? (
                    <img
                      src={n.actor_picture}
                      alt=""
                      style={{
                        width: 32, height: 32, borderRadius: "50%",
                        objectFit: "cover", flexShrink: 0, marginTop: 2,
                      }}
                    />
                  ) : (
                    <div style={{
                      width: 32, height: 32, borderRadius: "50%",
                      background: n.type === "like" ? "#ba3f9c" : "#6366f1",
                      color: "#fff",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 14, flexShrink: 0, marginTop: 2,
                    }}>
                      {TYPE_ICONS[n.type] || "?"}
                    </div>
                  )}

                  {/* Content */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{
                      fontSize: 13, color: "var(--ink)", margin: 0,
                      lineHeight: 1.4,
                      fontWeight: n.is_read ? 400 : 600,
                    }}>
                      {n.message}
                    </p>
                    <span style={{ fontSize: 11, color: "var(--muted, #888)" }}>
                      {formatTimestamp(n.created_at)}
                    </span>
                  </div>

                  {/* Unread dot */}
                  {!n.is_read && (
                    <span style={{
                      width: 8, height: 8, borderRadius: "50%",
                      background: "var(--brand, #59c6c1)",
                      flexShrink: 0, marginTop: 8,
                    }} />
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
